import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import StaffTermsForm from './stafftermsform';
import OnboardStaff from '../staff/onboardstaff';
import InstallButton from './downloadbutton';
import BackgroundLogins from './backgroundlogins';

const API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.insightos.co.uk'
    : 'https://api.insightos.co.uk';

export default function Authentication() {
  const { token = '' } = useParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [setFirstLogin, setSetFirstLogin] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showVideoScreen, setShowVideoScreen] = useState(false);
  const [submittingTerms, setSubmittingTerms] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false); // State to toggle password visibility
  const [verifyPasswordVisible, setVerifyPasswordVisible] = useState(false); // State to toggle verify password visibility
  const [showResetRequest, setShowResetRequest] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCompanyCode, setResetCompanyCode] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const forceRelogin = async () => {
    try {
      await fetch(`${API_BASE}/api/logoutboot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch (error) {
      // Ignore logout API failures; we still clear client state below.
      console.warn('Logout cleanup failed during staff verification flow', error);
    }

    try {
      localStorage.removeItem('token');
      localStorage.removeItem('onboardStep');
      sessionStorage.removeItem('last_reload_ts');
    } catch (storageError) {
      console.warn('Storage cleanup failed during staff verification flow', storageError);
    }

    window.location.assign('/');
  };

  const location = useLocation();

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
      setResetEmail(location.state.email);
    }
    if (location.state?.companyCode) {
      setResetCompanyCode(location.state.companyCode);
    }
  }, [location.state]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!token) {
      setErrorMessage('This verification link is missing a token. Please request a new verification email.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/verifystaff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          Email: email,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setSetFirstLogin(true);
        setLoginEmail(email);
        setCompanyCode(data.companyCode);
        setShowResetRequest(false);
      } else {
        setErrorMessage(data?.message || 'Verification failed. Please request a new verification link.');
      }
    } catch (error) {
      console.error('Failed to verify code', error);
      setErrorMessage('Failed to verify link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (password !== verifyPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/userfirstlogin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Email: loginEmail || email,
          Password: password,
          CompanyCode: companyCode,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        setShowTerms(true);
      } else {
        setErrorMessage(data?.message || 'Failed to create password.');
      }
    } catch (error) {
      console.error('Failed to login', error);
      setErrorMessage('Failed to create password.');
    } finally {
      setLoading(false);
    }
  };

  const handleTermsSubmit = async () => {
    setSubmittingTerms(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/termsstaff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Email: email,
          CompanyCode: companyCode,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowVideoScreen(true);
        return;
      }

      setErrorMessage(data.message || 'Failed to save terms acceptance');
    } catch (error) {
      console.error('Failed to save terms acceptance', error);
      setErrorMessage('Failed to save terms acceptance');
    } finally {
      setSubmittingTerms(false);
    }
  };

  const handleResetCodeRequest = async (event) => {
    event.preventDefault();
    setResetSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE}/api/resetcodestaff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Email: resetEmail,
          CompanyCode: resetCompanyCode,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setErrorMessage('A new verification link has been sent to your email.');
        setEmail(resetEmail);
        setShowResetRequest(false);
        return;
      }

      setErrorMessage(data.message || 'Failed to request a new verification link');
    } catch (error) {
      console.error('Failed to request new verification code', error);
      setErrorMessage('Failed to request a new verification link');
    } finally {
      setResetSubmitting(false);
    }
  };

  if (showVideoScreen) {
    return <OnboardStaff />;
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-8 bg-slate-950">
      <style>{`
        @keyframes staffVerificationFloatA {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(36px, -42px); }
        }
        @keyframes staffVerificationFloatB {
          0%, 100% { transform: translate(0px, 0px); }
          50% { transform: translate(-30px, 36px); }
        }
      `}</style>
      <div className="pointer-events-none absolute -top-28 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-3xl" style={{ animation: 'staffVerificationFloatA 24s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute -bottom-24 -right-20 h-[380px] w-[380px] rounded-full bg-violet-600/20 blur-3xl" style={{ animation: 'staffVerificationFloatB 28s ease-in-out infinite' }} />
      <BackgroundLogins />
      <InstallButton />

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/75 px-6 py-7 shadow-2xl backdrop-blur-xl sm:px-8">
        <div className="text-center">
          <img alt="InsightOS" src="/2.png" className="mx-auto h-14 w-14 rounded-full object-cover bg-indigo-900 border-2 border-white/20 shadow-lg" />

          {!token && !setFirstLogin && (
            <div className="mt-4 rounded-xl border border-red-300/20 bg-red-500/10 px-4 py-3 text-sm text-red-100 text-left">
              This verification link is invalid. Please request a new verification email.
            </div>
          )}

          {!setFirstLogin ? (
            <>
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-white">
                {showResetRequest ? 'Request New Verification Link' : 'Verify Your Account'}
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                {showResetRequest
                  ? 'Enter your email and company code to get a new verification link.'
                  : 'Enter your email to activate your account from this secure link.'}
              </p>

              <button
                    type="button"
                    onClick={() => {
                      setResetEmail(email);
                      setShowResetRequest(true);
                    }}
                    className="mt-4 w-full rounded-xl py-3 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition"
                  >
                    Request New Link
                  </button>

              {errorMessage && (
                <div className="mt-4 rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100 text-left">
                  {errorMessage}
                </div>
              )}

              {showResetRequest ? (
                <form onSubmit={handleResetCodeRequest} className="mt-6 space-y-4 text-left">
                  <div>
                    <label htmlFor="resetEmail" className="mb-2 block text-sm font-medium text-white">
                      Email Address
                    </label>
                    <input
                      id="resetEmail"
                      name="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label htmlFor="resetCompanyCode" className="mb-2 block text-sm font-medium text-white">
                      Company Code
                    </label>
                    <input
                      id="resetCompanyCode"
                      name="resetCompanyCode"
                      type="text"
                      value={resetCompanyCode}
                      onChange={(e) => setResetCompanyCode(e.target.value)}
                      required
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="Enter your company code"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={resetSubmitting}
                    className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                      resetSubmitting
                        ? 'cursor-not-allowed bg-indigo-500/40'
                        : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                    }`}
                  >
                    {resetSubmitting ? 'Sending...' : 'Send New Link'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowResetRequest(false)}
                    className="w-full rounded-xl py-3 text-sm font-semibold text-white bg-white/10 hover:bg-white/20 transition"
                  >
                    Back to Verification
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="mt-6 space-y-4 text-left">
                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-white">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="Enter your email"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !token}
                    className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                      loading || !token
                        ? 'cursor-not-allowed bg-indigo-500/40'
                        : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                    }`}
                  >
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </button>
                </form>
              )}
            </>
          ) : showTerms ? (
            <>
              <div className="mt-6 p-3 rounded-lg bg-white/10 border border-white/20 text-left">
                <div className="flex justify-between items-center text-sm text-slate-300">
                  <span>Email:</span>
                  <span className="font-medium">{loginEmail || email}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-300 mt-2">
                  <span>Company Code:</span>
                  <span className="font-medium">{companyCode}</span>
                </div>
              </div>
              {errorMessage && (
                <div className="mt-4 rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100 text-left">
                  {errorMessage}
                </div>
              )}
              <StaffTermsForm onSubmit={handleTermsSubmit} submitting={submittingTerms} />
            </>
          ) : (
            <>
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-white">
                Create Your Password
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Welcome to InsightOS! Let's set up your account.
              </p>

              <div className="mt-6 p-3 rounded-lg bg-white/10 border border-white/20 text-left">
                <div className="flex justify-between items-center text-sm text-slate-300">
                  <span>Email:</span>
                  <span className="font-medium">{loginEmail}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-slate-300 mt-2">
                  <span>Company Code:</span>
                  <span className="font-medium">{companyCode}</span>
                </div>
              </div>

              <form onSubmit={handlePassword} className="mt-6 space-y-4 text-left">
                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-white">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={passwordVisible ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="Create a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-xs text-slate-400 hover:text-white focus:outline-none"
                    >
                      {passwordVisible ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7A9.97 9.97 0 014.02 8.971m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="verifyPassword" className="mb-2 block text-sm font-medium text-white">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="verifyPassword"
                      name="verifyPassword"
                      type={verifyPasswordVisible ? 'text' : 'password'}
                      value={verifyPassword}
                      onChange={(e) => setVerifyPassword(e.target.value)}
                      required
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-sm text-white placeholder-slate-400 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setVerifyPasswordVisible(!verifyPasswordVisible)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-xs text-slate-400 hover:text-white focus:outline-none"
                    >
                      {verifyPasswordVisible ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7A9.97 9.97 0 014.02 8.971m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {errorMessage && (
                  <div className="rounded-xl border border-red-300/20 bg-red-500/10 px-3 py-2 text-xs text-red-100">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                    loading
                      ? 'cursor-not-allowed bg-indigo-500/40'
                      : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                  }`}
                >
                  {loading ? 'Saving...' : 'Complete Setup'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
